import { VEHICLE_CLASSES } from "./vehicleClasses";

/**
 * Verwendungs-Katalog der Kfz-Erfassung — reine Daten, ohne React.
 *
 * Bewusst getrennt vom `VerwendungPicker`: Nicht jede Fahrzeugklasse hat
 * einen Verwendungskatalog. Fehlt er, zeigt der Picker nur einen Hinweis und
 * der Nutzer kann `vehicleUsage` gar nicht befuellen — deshalb muss auch das
 * Schema wissen, fuer welche Klassen die Frage ueberhaupt zaehlt
 * (`VEHICLE_USAGE_CLASSES`). Ein React-Import wuerde das Schema in jeden
 * Logik-Test ziehen; darum liegt der Katalog hier.
 *
 * Der Katalog folgt der Fach-Excel:
 *   PKW          → Eigenverwendung, Auslieferungs-/Kurierdienst
 *   Lieferwagen  → Privat-/Werkverkehr, gewerblicher Gueterverkehr
 *   LKW          → Werkverkehr, gewerblicher Gueterverkehr
 *   Zugmaschine  → Werkverkehr, gewerblicher Gueterverkehr, Land-/Forstwirtschaft
 *   Anhaenger    → Privat-/Werkverkehr, gewerblicher Gueterverkehr,
 *                  Land-/Forstwirtschaft, Wohnwagenanhaenger
 */

export type VerwendungIconKey =
  | "eigen"
  | "kurier"
  | "werkverkehr"
  | "gueter"
  | "land"
  | "wohnwagen";

export type VerwendungOption = {
  value: string;
  label: string;
  desc?: string;
  iconKey: VerwendungIconKey;
};

const VERWENDUNGEN_BY_CLASS: Record<string, VerwendungOption[]> = {
  pkw: [
    { value: "Eigenverwendung", label: "Eigenverwendung", desc: "Privat & geschäftlich", iconKey: "eigen" },
    { value: "Auslieferungs-/Kurierdienst", label: "Auslieferung / Kurier", desc: "Liefer- & Kurierfahrten", iconKey: "kurier" },
  ],
  lieferwagen: [
    { value: "Privat- und Werkverkehr", label: "Privat- & Werkverkehr", desc: "Eigene Güter / Personen", iconKey: "werkverkehr" },
    { value: "gewerblicher Güterverkehr", label: "Gewerblicher Güterverkehr", desc: "Transport für Dritte", iconKey: "gueter" },
  ],
  lkw: [
    { value: "Werkverkehr", label: "Werkverkehr", desc: "Eigene Güter", iconKey: "werkverkehr" },
    { value: "gewerblicher Güterverkehr", label: "Gewerblicher Güterverkehr", desc: "Transport für Dritte", iconKey: "gueter" },
  ],
  zugmaschine: [
    { value: "Werkverkehr", label: "Werkverkehr", desc: "Eigene Güter", iconKey: "werkverkehr" },
    { value: "gewerblicher Güterverkehr", label: "Gewerblicher Güterverkehr", desc: "Transport für Dritte", iconKey: "gueter" },
    { value: "Land- & Forstwirtschaft", label: "Land- & Forstwirtschaft", desc: "Landwirtschaftlich genutzt", iconKey: "land" },
  ],
  anhaenger: [
    { value: "Privat- und Werkverkehr", label: "Privat- & Werkverkehr", desc: "Eigene Güter / Personen", iconKey: "werkverkehr" },
    { value: "gewerblicher Güterverkehr", label: "Gewerblicher Güterverkehr", desc: "Transport für Dritte", iconKey: "gueter" },
    { value: "Land- & Forstwirtschaft", label: "Land- & Forstwirtschaft", desc: "Landwirtschaftlich genutzt", iconKey: "land" },
    { value: "Wohnwagenanhänger", label: "Wohnwagenanhänger", desc: "Camping / Freizeit", iconKey: "wohnwagen" },
  ],
};

const classifyVehicleClass = (
  vehicleClass: string
): keyof typeof VERWENDUNGEN_BY_CLASS | null => {
  if (vehicleClass.includes("Klasse 1") || /pkw/i.test(vehicleClass)) return "pkw";
  if (vehicleClass.includes("Klasse 2") || /lieferwagen/i.test(vehicleClass)) return "lieferwagen";
  if (vehicleClass.includes("Klasse 3") || /lkw/i.test(vehicleClass)) return "lkw";
  if (vehicleClass.includes("Klasse 4") || /zugmaschine/i.test(vehicleClass)) return "zugmaschine";
  if (vehicleClass.includes("Klasse 6") || /anh[aä]nger/i.test(vehicleClass)) return "anhaenger";
  return null;
};

/** Hat die Fahrzeugklasse ueberhaupt Verwendungsoptionen? */
export const hasVerwendungOptions = (vehicleClass: string) =>
  !!classifyVehicleClass(vehicleClass);

export const verwendungOptionsFor = (vehicleClass: string): VerwendungOption[] => {
  const key = classifyVehicleClass(vehicleClass);
  return key ? VERWENDUNGEN_BY_CLASS[key] : [];
};

/**
 * Die Fahrzeugklassen, in denen der Picker tatsaechlich eine Auswahl anbietet
 * — abgeleitet, damit Katalog und Schema-Gate nicht auseinanderlaufen koennen.
 *
 * Ergibt PKW, Lieferwagen, LKW, Zugmaschine, Anhaenger — und, weil
 * "Klasse 10 Sonderfahrzeuge" die Pruefung `includes("Klasse 1")` mit trifft,
 * auch die Sonderfahrzeuge mit dem PKW-Katalog. Das ist Bestand: Die
 * Fach-Liste (RISIKO_QUESTIONS in kfzContractStep/data.ts) kennt fuer
 * Klasse 10 keine Verwendung. Bewusst nicht hier repariert — das Gate soll
 * exakt das abbilden, was der Picker anbietet; ob Sonderfahrzeuge eine
 * Verwendung bekommen sollen, ist eine fachliche Frage.
 */
export const VEHICLE_USAGE_CLASSES: string[] = VEHICLE_CLASSES.map(
  (c) => c.value
).filter(hasVerwendungOptions);
